import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { MoreHorizontal } from "lucide-react";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import {
  useTableSavedViews,
  TableSavedViewsControl,
  TABLE_PAGE_KEYS,
} from "../../../TableSavedViews";
import {
  ClaimStatusBadge,
  ClaimTypeBadge,
  formatClaimMoney,
  formatClaimDate,
} from "../claimsShared";
import { useClaimsListData } from "../hooks/useClaimsListData";
import {
  CLAIM_SORTABLE_COLUMNS,
  CLAIM_TABLE_HEADERS,
  DEFAULT_CLAIM_SORT_BY,
} from "./claimsTableConfig";

const tdBase =
  "px-2 sm:px-3 py-1 sm:py-2 " +
  "text-xs sm:text-sm md:text-base " +
  "text-gray-900 dark:text-acs-dark-text " +
  "border-b border-gray-200 dark:border-acs-dark-border " +
  "transition-none font-roboto";

const linkClass =
  "font-semibold text-[#223688] dark:text-[#6DE1C2] hover:text-acs-primary-blue hover:underline bg-transparent border-none p-0 cursor-pointer transition-none";

const ClaimsTable = ({
  access,
  refreshKey = 0,
  onSelectClaim,
  onShipmentClick,
  statusFilter,
  onStatusFilterChange,
  typeFilter,
  onTypeFilterChange,
  searchQuery,
  onTotalClaimsChange,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState(DEFAULT_CLAIM_SORT_BY);
  const [ascending, setAscending] = useState(false);
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const dropdownRef = useRef(null);
  const checkboxHeaderRef = useRef(null);

  const getExtraConfig = useCallback(
    () => ({
      sortBy,
      ascending,
      currentPage,
      pageSize,
      statusFilter,
      typeFilter,
    }),
    [sortBy, ascending, currentPage, pageSize, statusFilter, typeFilter]
  );

  // Set while a saved view's filters are being applied so the change-detection
  // effect below doesn't mark the just-applied view dirty against itself.
  const isApplyingSavedViewRef = useRef(false);

  const handleApplySavedViewConfig = useCallback((config) => {
    isApplyingSavedViewRef.current = true;
    if (config.sortBy !== undefined) setSortBy(config.sortBy || DEFAULT_CLAIM_SORT_BY);
    if (config.ascending !== undefined) setAscending(Boolean(config.ascending));
    if (config.currentPage) setCurrentPage(config.currentPage);
    if (config.pageSize) setPageSize(config.pageSize);
    if (config.statusFilter !== undefined) onStatusFilterChange?.(config.statusFilter);
    if (config.typeFilter !== undefined) onTypeFilterChange?.(config.typeFilter);
  }, [onStatusFilterChange, onTypeFilterChange]);

  const tableViews = useTableSavedViews({
    pageKey: TABLE_PAGE_KEYS.CLAIMS_LIST,
    allHeaders: CLAIM_TABLE_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", "Claim Number"],
    getExtraConfig,
    onApplyConfig: handleApplySavedViewConfig,
  });

  const {
    columnWidths: savedColumnWidths,
    setColumnWidths: onColumnResize,
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    markDirty,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  // Reset to page 1 when filters/search change (render-time, Invoice pattern)
  const [prevStatusForPageReset, setPrevStatusForPageReset] = useState(statusFilter);
  const [prevTypeForPageReset, setPrevTypeForPageReset] = useState(typeFilter);
  const [prevSearchForPageReset, setPrevSearchForPageReset] = useState(searchQuery);
  let effectivePage = currentPage;
  if (
    prevStatusForPageReset !== statusFilter ||
    prevTypeForPageReset !== typeFilter ||
    prevSearchForPageReset !== searchQuery
  ) {
    setPrevStatusForPageReset(statusFilter);
    setPrevTypeForPageReset(typeFilter);
    setPrevSearchForPageReset(searchQuery);
    if (!isApplyingSavedViewRef.current) {
      markDirty?.();
    }
    isApplyingSavedViewRef.current = false;
    if (currentPage !== 1) {
      effectivePage = 1;
      setCurrentPage(1);
    }
  }

  const {
    claims,
    totalElements,
    totalPages,
    showLoadingOverlay,
    errorMessage,
  } = useClaimsListData({
    officeCode: access?.officeCodes?.join(",") || access?.officeCode,
    statusFilter,
    typeFilter,
    searchQuery,
    page: effectivePage,
    size: pageSize,
    sortBy,
    ascending,
    refreshKey,
    onPageOutOfRange: setCurrentPage,
  });

  useEffect(() => {
    setSelectedClaims([]);
    setOpenMenu(null);
  }, [claims]);

  // Report the count up to the module-level row (mirrors Shipment's setTotalShipments).
  useEffect(() => {
    onTotalClaimsChange?.(totalElements);
  }, [totalElements, onTotalClaimsChange]);

  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const handleSort = (headerLabel) => {
    markDirty?.();
    const key = CLAIM_SORTABLE_COLUMNS[headerLabel];
    if (!key) return;
    setCurrentPage(1);
    if (sortBy === key) {
      setAscending((prev) => !prev);
    } else {
      setSortBy(key);
      setAscending(true);
    }
  };

  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setOpenMenu((prev) => (prev === id ? null : id));
  };

  const toggleClaimSelection = (id) => {
    setSelectedClaims((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked) => {
    if (!checked) {
      setSelectedClaims([]);
      return;
    }
    setSelectedClaims(claims.map((c) => c.claimNumber));
  };

  const rows = useMemo(
    () =>
      claims.map((claim) => ({
        _id: claim.claimNumber,
        _claim: claim,
        Selection: claim.claimNumber,
        Action: claim,
        "Claim Number": (
          <button
            type="button"
            className={linkClass}
            onClick={(e) => {
              e.stopPropagation();
              onSelectClaim?.(claim);
            }}
          >
            {claim.claimNumber}
          </button>
        ),
        "Shipment Number": (
          <button
            type="button"
            className={linkClass}
            onClick={(e) => {
              e.stopPropagation();
              onShipmentClick?.(claim);
            }}
          >
            {claim.shipmentNumber || "—"}
          </button>
        ),
        "PO Number": claim.poNumber || "—",
        "Claim Type": <ClaimTypeBadge type={claim.claimType} />,
        Status: <ClaimStatusBadge status={claim.status} />,
        "Related Stop": claim.relatedStop || "—",
        "Date Filed": formatClaimDate(claim.dateFiled),
        "Total Claim Amount": formatClaimMoney(claim.totalClaimAmount),
        "Amount Approved": formatClaimMoney(claim.amountApproved),
        "Amount Paid": formatClaimMoney(claim.amountPaid),
        "Office Code": claim.officeCode || claim.shipmentOfficeCode || "—",
      })),
    [claims, onSelectClaim, onShipmentClick]
  );

  return (
    <div className="flex flex-col flex-1 h-full min-h-0 font-roboto bg-white dark:bg-acs-dark-surface">
      {errorMessage && (
        <div className="shrink-0 px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {errorMessage}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
        <TableSavedViewsControl
          controlLabel={controlLabel}
          views={views}
          activeView={activeView}
          loading={viewsLoading}
          error={viewsError}
          isDirty={isDirty}
          allDataHeaders={CLAIM_TABLE_HEADERS.slice(2)}
          dataHeaders={dataHeaders}
          onSelectView={selectView}
          onSaveNew={saveNewView}
          onUpdateActive={updateActiveView}
          onRename={renameView}
          onDelete={deleteView}
          onSetDefault={setDefaultView}
          onUpdateColumnVisibility={updateColumnVisibility}
          onManageColumns={onManageColumns}
        />
      </div>

      <div className="relative flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
        {showLoadingOverlay && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 dark:bg-acs-dark-surface/60">
            <Loader
              color="text-blue-600 dark:text-acs-dark-text"
              message="Loading Claims…"
            />
          </div>
        )}

        <Table
          {...columnManagerProps}
          data={rows}
          selectedRows={selectedClaims}
          totalValidRows={rows.length}
          onSelectAll={handleSelectAll}
          checkboxHeaderRef={checkboxHeaderRef}
          enableColumnPin
          enableColumnResize
          enableColumnReorder
          dataColumnMinWidth={180}
          dataColumnMaxWidth={420}
          initialColumnWidths={savedColumnWidths}
          onColumnResize={onColumnResize}
          nonHideableColumnIds={["Claim Number"]}
          sortableHeaders={Object.keys(CLAIM_SORTABLE_COLUMNS)}
          onSort={handleSort}
          sortBy={sortBy}
          ascending={ascending}
          sortKeyByHeader={(label) => CLAIM_SORTABLE_COLUMNS[label]}
        >
          {(row, _rowIdx, colWidths, getStickyCellProps, visibleColumns) => {
            const id = row._id;
            const claim = row._claim;
            const isOpen = openMenu === id;

            const { className: checkboxClass, style: checkboxStyle } =
              getStickyCellProps("Selection");
            const { className: actionClass, style: actionStyle } =
              getStickyCellProps("Action");

            const orderedDataHeaders = (visibleColumns ?? [])
              .map((col) => col.id)
              .filter((cid) => cid !== "Selection" && cid !== "Action");
            const dataHeadersToRender = orderedDataHeaders.length
              ? orderedDataHeaders
              : dataHeaders;

            return (
              <>
                <td
                  className={`border-b border-gray-200 dark:border-acs-dark-border z-[10] text-center transition-none ${checkboxClass}`}
                  style={{
                    ...checkboxStyle,
                    width: "80px",
                    minWidth: "80px",
                    maxWidth: "80px",
                  }}
                >
                  <input
                    type="checkbox"
                    className="
                      relative appearance-none cursor-pointer mx-auto block
                      w-[15px] h-[15px] rounded-sm
                      border border-gray-400 dark:border-acs-dark-border
                      bg-white dark:bg-acs-dark-surface
                      transition-none
                      checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark
                      checked:border-acs-checkbox dark:checked:border-acs-checkbox-dark
                      after:content-[''] after:absolute after:inset-0
                      after:flex after:items-center after:justify-center
                      checked:after:content-['✓']
                      checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                    checked={selectedClaims.includes(id)}
                    onChange={() => toggleClaimSelection(id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>

                <td
                  onClick={(e) => e.stopPropagation()}
                  className={`border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${actionClass}`}
                  style={{
                    ...actionStyle,
                    width: "50px",
                    minWidth: "50px",
                    maxWidth: "50px",
                  }}
                >
                  <button
                    type="button"
                    data-action-trigger
                    className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                    onClick={(e) => handleActionClick(id, e)}
                  >
                    <MoreHorizontal size={18} className="mx-auto" />
                  </button>
                </td>

                {isOpen &&
                  ReactDOM.createPortal(
                    <div
                      ref={dropdownRef}
                      style={{
                        position: "absolute",
                        top: dropdownPos.top,
                        left: dropdownPos.left,
                        zIndex: 9999,
                      }}
                    >
                      <ActionMenu
                        rowId={id}
                        isOpen
                        moduleName="Claims"
                        onClose={() => setOpenMenu(null)}
                        actions={[
                          {
                            icon: "Eye",
                            label: "View Claim",
                            color: "blue",
                            action: () => onSelectClaim?.(claim),
                          },
                          {
                            icon: "FilePlus",
                            label: "Open Shipment",
                            color: "gray",
                            action: () => onShipmentClick?.(claim),
                          },
                        ]}
                      />
                    </div>,
                    document.body
                  )}

                {dataHeadersToRender.map((header) => {
                  const { className: colClass, style: colStyle } =
                    getStickyCellProps(header);
                  const colWidth = colWidths?.[header] ?? 200;
                  return (
                    <td
                      key={header}
                      className={`${tdBase} ${colClass}`}
                      style={{
                        ...colStyle,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: `${colWidth}px`,
                        minWidth: `${colWidth}px`,
                        maxWidth: `${colWidth}px`,
                      }}
                      onDoubleClick={() => onSelectClaim?.(claim)}
                    >
                      {row[header]}
                    </td>
                  );
                })}
              </>
            );
          }}
        </Table>

        {!showLoadingOverlay && claims.length === 0 && (
          <div className="px-3 py-8 text-center text-gray-500 dark:text-acs-dark-subtext text-fluid-sm">
            {access?.isCarrier
              ? "No claims found on your shipments."
              : "No claims match your filters."}
          </div>
        )}
      </div>

      <GlobalPagination
        label="Claims per page"
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={pageSize}
        onItemsPerPageChange={(n) => {
          setPageSize(n);
          setCurrentPage(1);
        }}
      />
    </div>
  );
};

export default ClaimsTable;
